import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';

import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';

const PersonalProjectsTutorial = () => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const handleReset = () => {
    setActiveStep(0);
  };
  const steps = [
    {
      label: 'Select an AI Persona',
      description: `Choose from specialized AI experts like DIYMind or Practical Craftsman that best matches your project needs. First click on the "Learn More" button and 
      then select the persona. Each persona has unique expertise in different areas of DIY.`,
      image: '/select_personal_persona.png',
      tip: 'Hover over "Learn More" to see details about each persona\'s expertise before selecting.'
    },
    {
      label: 'Create Your Project',
      description: `Enter your project name and a detailed description of what you want to build. Be specific about materials, style 
preferences, and any constraints like budget or timeline.`,
      image: '/create_personal_project.png',
      tip: 'The more detail you provide in your project description, the more personalized your blueprint will be.'
    },
    {
      label: 'Select Your Project',
      description: `After creating your project, select it from the dropdown menu in the "Your Personal Projects" section to activate it for the 
next steps.`,
      image: '/sleect_personal_project.png',
      tip: 'Your most recently created project will appear at the top of the list.'
    },
    {
      label: 'Answer Persona Questions',
      description: `Your chosen AI persona will guide you through a two-part questionnaire. First, you'll answer 10 initial questions about your project requirements and preferences. After question 10, your AI will process this information to create an initial project assessment. Then, you'll answer 5 more detailed questions to refine your blueprint.`,
      image: '/answer_personal_question.png',
      tip: 'Provide thoughtful answers to each question as these will directly influence your blueprint quality. Look for the "Processing Specifications" message after question 10 - this indicates your AI is building the initial project framework.'
    },
    {
      label: 'Fetch Project Blueprint',
      description: `Click the "Fetch Project Blueprint" button to generate your comprehensive project plan. This may take a moment as your AI 
persona creates a custom blueprint.`,
      image: '/fectch_personal_project.png',
      tip: 'The blueprint is automatically saved, so you only need to fetch it once per project.'
    },
    {
      label: 'View Your Blueprint',
      description: `Click "Show Blueprint" to display your complete project plan, including materials, steps, cost estimates, and recommended 
products. Scroll down to see product recommendations.`,
      image: '/show_personal_project.png',
      tip: 'You can toggle between showing and hiding the blueprint for easier navigation.'
    },
    {
      label: 'Toggle Follow-Up Question',
      description: `Click the "Ask Follow-Up Question" button to open the popup chat interface. This button appears when you have an active project selected.`,
      image: '/click_ask_question.png',
      tip: 'The button toggles between showing and hiding the follow-up question form, keeping your workspace clean until you need it.'
    },
    {
      label: 'Chat With Your AI Persona',
      description: `Once the follow-up question form is open, ask specific questions about your project and your AI persona will provide expert advice based on your project blueprint.`,
      image: '/toggle_question.png',
      tip: 'Ask clear, specific questions about materials, techniques, or modifications to get the most helpful responses.'
    }
  ];
  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Help"
        color="primary"
        sx={{ 
          position: 'fixed',
          bottom: 20,
          right: 20,
          bgcolor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          '&:hover': {
            bgcolor: '#f0f7ff',
          },
          zIndex: 1000,
          width: 60,
          height: 60
        }}
      >
        <HelpOutlineIcon fontSize="large" />
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1e3c72',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            How to Create Projects with AI Personas
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" paragraph>
              Follow these steps to create a personalized project blueprint with your chosen AI expert:
            </Typography>
          </Box>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" paragraph>
                      {step.description}
                    </Typography>
                    
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        bgcolor: '#f0f7ff', 
                        borderRadius: 2,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                      }}
                    >
                      <InfoIcon color="primary" sx={{ mt: 0.5 }} />
                      <Typography variant="body2" color="primary.dark">
                        <strong>Tip:</strong> {step.tip}
                      </Typography>
                    </Paper>
                    
                    <Paper
                      elevation={3}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 2,
                        maxWidth: 500,
                        mx: 'auto'
                      }}
                    >
                      <Box
                        component="img"
                        src={step.image}
                        alt={`Step ${index + 1}: ${step.label}`}
                        sx={{
                          width: '100%',
                          height: 'auto'
                        }}
                      />
                    </Paper>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      variant="outlined"
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={index === steps.length - 1 ? handleClose : handleNext}
                      sx={{ 
                        background: index === steps.length - 1 
                          ? 'linear-gradient(90deg, #2a5298 0%, #1e3c72 100%)' 
                          : undefined 
                      }}
                    >
                      {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
          {activeStep === steps.length && (
            <Paper square elevation={0} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                All steps completed! You're ready to create projects with AI experts.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button onClick={handleReset} variant="outlined">
                  Review Steps Again
                </Button>
                <Button onClick={handleClose} variant="contained">
                  Start Building
                </Button>
              </Box>
            </Paper>
          )}
          <Divider sx={{ my: 4 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e3c72' }}>
              Quick Reference Guide
            </Typography>
            <Box 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 3,
                mt: 2
              }}
            >
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <PersonIcon sx={{ fontSize: 35, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  1. Choose Persona
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Select an AI expert with specialization in your project area
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <DescriptionIcon sx={{ fontSize: 35, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  2. Describe Project
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Provide project name and detailed description
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <QuestionAnswerIcon sx={{ fontSize: 35, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  3. Answer Questions
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Respond to your AI expert's specific questions
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  gridColumn: { md: '1 / 2' }
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 35, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  4. Generate Blueprint
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Fetch your customized project blueprint
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  gridColumn: { md: '2 / 3' }
                }}
              >
                <VisibilityIcon sx={{ fontSize: 35, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  5. View Blueprint
                </Typography>
                <Typography variant="body2" textAlign="center">
                  See your complete project plan and product recommendations
                </Typography>
              </Paper>
              
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  gridColumn: { md: '3 / 4' }
                }}
              >
                <SearchIcon sx={{ fontSize: 35, color: '#1e3c72' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  6. Ask Questions
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Toggle the follow-up question box to chat with your AI expert
                </Typography>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PersonalProjectsTutorial;